import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { skip } from 'rxjs/operators';
import {
  PermissionSyncPayload,
  PERMISSION_STORAGE_KEYS,
  SyncedRole,
} from './permission.types';

/**
 * Reactive store for the current user's effective permissions.
 *
 * Previously permissions lived only as a comma-joined localStorage string written
 * once by LoginComponent, so a permission change could not reach the UI without a
 * re-login. This service is now the single source of truth: everything that gates
 * on a permission reads through it, and every consumer can subscribe to changes.
 *
 * It still MIRRORS to the same localStorage keys, for three reasons:
 *   1. permissions survive a page reload before the first sync completes,
 *   2. the `storage` event gives cross-tab propagation for free,
 *   3. code that has not been migrated to the observable keeps working.
 *
 * SECURITY: this is a UX cache, never an authority. The backend re-checks every
 * request; nothing here is trusted for enforcement.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private permissionsSubject = new BehaviorSubject<string[]>(this.readPermissions());
  private rolesSubject = new BehaviorSubject<SyncedRole[]>(this.readRoles());
  private superAdminSubject = new BehaviorSubject<boolean>(this.readIsSuperAdmin());

  /** Current permissions, replaying the latest value to new subscribers. */
  readonly permissions$: Observable<string[]> = this.permissionsSubject.asObservable();

  /**
   * Emits only on an actual CHANGE, skipping the replayed initial value.
   * Use this for "react to a permission change" (re-filter menu, re-check the
   * active route); use permissions$ for "render according to permissions".
   */
  readonly changes$: Observable<string[]> = this.permissionsSubject.pipe(skip(1));

  private lastIssuedAt = 0;

  // ── Snapshots (synchronous readers) ────────────────────────────────────

  snapshot(): string[] {
    return this.permissionsSubject.value;
  }

  roles(): SyncedRole[] {
    return this.rolesSubject.value;
  }

  /** Role ids used to decide whether an incoming signal affects this user. */
  roleIds(): number[] {
    return this.rolesSubject.value
      .map((role) => Number(role?.id))
      .filter((id) => !isNaN(id) && id > 0);
  }

  isSuperAdmin(): boolean {
    return this.superAdminSubject.value;
  }

  // ── Mutation ───────────────────────────────────────────────────────────

  /**
   * Apply a fresh payload from the permissions-sync endpoint.
   * Returns false when the payload was rejected as stale.
   */
  apply(payload: PermissionSyncPayload): boolean {
    if (!payload || !Array.isArray(payload.permissions)) {
      return false;
    }

    // Two role changes in quick succession can produce two in-flight requests
    // whose responses land out of order. Keep only the newest.
    const issuedAt = Number(payload.issued_at) || 0;
    if (issuedAt > 0 && issuedAt <= this.lastIssuedAt) {
      return false;
    }
    this.lastIssuedAt = issuedAt;

    const permissions = payload.permissions.map((p) => String(p));
    const roles = Array.isArray(payload.roles) ? payload.roles : [];
    const isSuperAdmin = Number(payload.is_super_admin) === 1;

    // Mirror first so a listener that reads localStorage synchronously (or a
    // sibling tab woken by the storage event) sees the new value, not the old.
    localStorage.setItem(PERMISSION_STORAGE_KEYS.permissions, String(permissions));
    localStorage.setItem(PERMISSION_STORAGE_KEYS.roles, JSON.stringify(roles));
    localStorage.setItem(PERMISSION_STORAGE_KEYS.isSuperAdmin, isSuperAdmin ? '1' : '0');

    this.rolesSubject.next(roles);
    this.superAdminSubject.next(isSuperAdmin);
    this.permissionsSubject.next(permissions);

    return true;
  }

  /**
   * Re-read from localStorage and emit if it differs. Used by the cross-tab
   * `storage` fallback, where another tab already wrote the new values.
   */
  rehydrateFromStorage(): void {
    const permissions = this.readPermissions();
    if (this.sameSet(permissions, this.permissionsSubject.value)) {
      return;
    }
    this.rolesSubject.next(this.readRoles());
    this.superAdminSubject.next(this.readIsSuperAdmin());
    this.permissionsSubject.next(permissions);
  }

  /** Clear on logout so a subsequent login never renders the previous user's UI. */
  clear(): void {
    this.lastIssuedAt = 0;
    this.rolesSubject.next([]);
    this.superAdminSubject.next(false);
    this.permissionsSubject.next([]);
  }

  // ── Storage readers ────────────────────────────────────────────────────

  private readPermissions(): string[] {
    const raw = localStorage.getItem(PERMISSION_STORAGE_KEYS.permissions);
    if (!raw) {
      return [];
    }
    return raw
      .split(',')
      .map((p) => p.trim())
      .filter((p) => !!p);
  }

  private readRoles(): SyncedRole[] {
    try {
      const parsed = JSON.parse(localStorage.getItem(PERMISSION_STORAGE_KEYS.roles) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private readIsSuperAdmin(): boolean {
    return localStorage.getItem(PERMISSION_STORAGE_KEYS.isSuperAdmin) === '1';
  }

  private sameSet(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const setB = new Set(b);
    return a.every((item) => setB.has(item));
  }
}
